import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMessage } from '../context/MessageContext';
import { messageAPI } from '../services/api';
import ChatBox from '../components/ChatBox';
import './MessageCenter.css';

const MessageCenter = () => {
  const { user } = useAuth();
  const { refreshUnreadCount } = useMessage();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [chatBoxOpen, setChatBoxOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadConversations(true);
  }, [user]);

  useEffect(() => {
    // Refresh unread count when conversations change
    refreshUnreadCount();
  }, [conversations, refreshUnreadCount]);

  const loadConversations = async (reset = false) => {
    try {
      const tokenToUse = reset ? null : nextToken;
      
      if (reset) {
        setLoading(true);
        setConversations([]);
        setNextToken(null);
      } else {
        setLoadingMore(true);
      }
      
      const response = await messageAPI.getConversations(20, tokenToUse);
      // Filter out conversations with "Unknown User" or missing otherUserId
      const validConversations = (response.data.conversations || []).filter(conv => {
        return conv.otherUserId && 
               conv.otherUserName && 
               conv.otherUserName !== 'Unknown User';
      });
      
      if (reset) {
        setConversations(validConversations);
      } else {
        setConversations(prev => [...prev, ...validConversations]);
      }
      
      // Update pagination state
      setNextToken(response.data.nextToken || null);
      setHasMore(!!response.data.nextToken);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadConversations(false);
    }
  };

  const handleConversationClick = (conversation) => {
    setSelectedConversation({
      userId: conversation.otherUserId,
      name: conversation.otherUserName,
    });
    setChatBoxOpen(true);
    
    // Mark conversation as read (pass conversationId from the conversation object)
    messageAPI.markConversationRead(conversation.otherUserId, conversation.conversationId)
      .then(() => {
        loadConversations(true); // Reset pagination when refreshing
        refreshUnreadCount();
      })
      .catch(err => console.error('Failed to mark conversation as read:', err));
  };

  const handleCloseChatBox = () => {
    setChatBoxOpen(false);
    setSelectedConversation(null);
    loadConversations(true); // Reset pagination when refreshing
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;
    
    return date.toLocaleDateString();
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (!message) return '';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  if (!user) {
    return null;
  }

  return (
    <div className="message-center">
      <div className="message-center-header">
        <h1>Messages</h1>
      </div>

      {loading ? (
        <div className="message-center-loading">Loading conversations...</div>
      ) : conversations.length === 0 ? (
        <div className="message-center-empty">
          <p>No conversations yet.</p>
          <p className="message-center-empty-hint">
            Start a conversation by clicking the message icon next to a player's name in any league.
          </p>
        </div>
      ) : (
        <>
          <div className="message-center-list">
            {conversations.map((conversation) => {
              const hasUnread = (conversation.unreadCount || 0) > 0;
              const otherUserName = conversation.otherUserName || 'Unknown User';
              const avatarInitial = otherUserName.charAt(0).toUpperCase();
              return (
                <div
                  key={conversation.conversationId}
                  className={`message-center-item ${hasUnread ? 'unread' : ''}`}
                  onClick={() => handleConversationClick(conversation)}
                >
                  <div className="message-center-item-avatar">
                    {avatarInitial}
                  </div>
                  <div className="message-center-item-content">
                    <div className="message-center-item-header">
                      <span className="message-center-item-name">
                        {otherUserName}
                      </span>
                      <span className="message-center-item-time">
                        {formatTime(conversation.lastMessageAt)}
                      </span>
                    </div>
                    <div className="message-center-item-preview">
                      <span className={`message-center-item-message ${hasUnread ? 'unread' : ''}`}>
                        {conversation.lastMessageSenderId === user.userId && 'You: '}
                        {truncateMessage(conversation.lastMessageContent)}
                      </span>
                      {hasUnread && (
                        <span className="message-center-item-badge">
                          {conversation.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {hasMore && (
            <div className="message-center-load-more">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="message-center-load-more-button"
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {chatBoxOpen && selectedConversation && (
        <ChatBox
          otherUserId={selectedConversation.userId}
          otherUserName={selectedConversation.name}
          onClose={handleCloseChatBox}
        />
      )}
    </div>
  );
};

export default MessageCenter;

